import { services } from '@/services';
import { detachAll as detachAllFirestoreListeners } from '@/services/firebase/listenerRegistry';
// R413 — 經 `session.ts`(不 import SDK)收 Firebase;SDK 沒載入過 = 沒有 Firebase session 可清。
import { resetLoadedFirebaseSignIn, signOutLoadedFirebase } from '@/services/firebase/session';
import { clearAllSessionStores } from '@/stores/sessionReset';

/**
 * clearClientAuthState — 主動登出時清空所有 client-side auth state(單一出口)
 * -----------------------------------------------------------------------------
 * 把 ThemeMenu「登出」按鈕原本 inline 的多步副作用收斂成一個函式,讓登出順序
 * 跟 `services/index.ts` 的 `onAuthLost`(401-driven 退場)**完全對齊**,避免兩條
 * auth-exit 路徑分歧。
 *
 * 順序固定(對齊 {@link ../../docs/decisions/ADR-002-firebase-auth-coexistence.md ADR-002}
 * §4 主動 Logout + §3 `onAuthLost`):
 *   1. `services.authProvider.signOut()` — BE revoke session + `auth.revoke_refresh_tokens(cuid)`
 *      (全 device 立即失效;漏這步 = N hr Firebase ID token auto-refresh 續命的 read 漏洞)
 *   2. `detachAllFirestoreListeners()` — 拆掉所有 onSnapshot,避免用過期 token 噴 permission-denied
 *   3. `firebaseAuth.signOut()` — 清 in-memory SDK state(IndexedDB stale token 清掉)
 *      (R413 起經 `signOutLoadedFirebase`;SDK 沒載入過就沒有 session,略過)
 *   4. `resetFirebaseSignInCache()` — reset `ensureFirebaseSignIn` 的 promise cache,
 *      避免下一個 user 在同分頁 re-login 時舊 promise 還 resolved 拿到**前一個 user** 的
 *      Firebase session(cross-user 破口;`onAuthLost` 有清,過去手動登出漏清)
 *   5. `clearAllSessionStores()` — 清 7 個 user-scoped store(auth / wizard / job / asset /
 *      group / wallet / catalog;見其 JSDoc 的「為什麼集中」)
 *
 * **不做 navigation**:跳轉 `/login` 由 caller 自行決定(手動登出走 `navigate`,
 * `onAuthLost` 走 RequireAuth 觀察 store 變化自然 redirect)。BE revoke 的網路錯誤
 * 不擋後續清理 — 即使 BE 不可達,本機 state 仍要清乾淨。
 */
export async function clearClientAuthState(): Promise<void> {
  // 1. BE revoke(失敗也要往下清本機 state — 不能因為 BE 不可達就把 user 卡在半登入狀態)
  try {
    await services.authProvider.signOut();
  } catch (err) {
    console.error('[auth] BE signOut failed', err);
  }
  // 2. detach all Firestore listener(在 signOut 之前拆,避免過期 token 噴 toast)
  detachAllFirestoreListeners();
  // 3. firebaseAuth.signOut() — 清 in-memory SDK state
  try {
    await signOutLoadedFirebase();
  } catch (err) {
    console.error('[auth] firebaseAuth signOut failed', err);
  }
  // 4. reset ensureFirebaseSignIn promise cache(對齊 onAuthLost;漏 = cross-user session 殘留)
  resetLoadedFirebaseSignIn();
  // 5. 清全部 user-scoped store(與 onAuthLost 共用單一出口)
  clearAllSessionStores();
}
